<template>
  <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
    <BaseCard class="relative">
      <BaseInnerLoading v-if="isSubmitting" />
      <p v-if="currentUser" class="text-gray-600">
        Setting new password for
        <strong>{{ currentUser.email }}</strong>
      </p>

      <SetChangeResetPasswordForm
        :require-current-password="requireCurrentPassword"
        user-type="merchant-user"
        @submit="onSubmit"
      />
    </BaseCard>
  </div>
</template>

<script setup lang="ts">
import { ApiError } from "@/api"
import type { SetChangeResetPasswordFormOutput } from "@/composables/forms/useSetChangeResetPasswordForm"
import type { SubmissionContext } from "vee-validate"

const { setPageTitle } = usePageTitle()
const router = useRouter()
const { currentUser, retrieveSelf } = useCurrentUser()

const pageTitle = computed(() =>
  currentUser.value?.hasPassword
    ? "Change Password"
    : "Set Password"
)
watchEffect(() => setPageTitle(pageTitle.value))

const isSubmitting = ref(false)

const requireCurrentPassword = computed(
  () => currentUser.value?.hasPassword ?? false
)

const onSubmit = async (
  params: SetChangeResetPasswordFormOutput,
  context: SubmissionContext
) => {
  isSubmitting.value = true
  const hadPassword = requireCurrentPassword.value
  try {
    await selfChangePassword({
      currentPassword: params.currentPassword,
      password: params.password,
    })

    await retrieveSelf()
    toast.success(
      hadPassword
        ? "Password changed successfully"
        : "Password set successfully"
    )

    router.push({ name: "dashboard" })
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.code === "invalid-current-password") {
        context.setFieldError(
          "currentPassword",
          "Current password is incorrect"
        )
        return
      }
      if (error.code === "current-password-required") {
        context.setFieldError(
          "currentPassword",
          "Current password is required"
        )
        return
      }
      if (
        error.code &&
        [
          "invalid-password",
          "same-as-current-password",
        ].includes(error.code)
      ) {
        context.setFieldError(
          "password",
          error.detail ||
            "Password does not meet requirements"
        )
        return
      }
    }
    toast.error("Failed to change password")
  } finally {
    isSubmitting.value = false
  }
}
</script>
